import React, { useEffect, useState } from "react";
import {
  Card,
  Modal,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Select,
  Table,
  Space,
  Tooltip,
  message,
  Popconfirm,
} from "antd";
import dayjs from "dayjs";
import {
  PlusOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import axiosInstance, { baseURL } from "../../../configs"; // sesuaikan path
import { getUser } from "../../../utils/DataUser";
import { canCreate, canEdit, canDelete, MODULES } from "../../../utils/rbac";

const { Title, Text } = Typography;


const defaultPageSize = 20;

export default function BrowseItem() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  // filter
  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);

  const [deletingId, setDeletingId] = useState(null);

  const apiBase = process.env.REACT_APP_PDF_URL || "/api";
  console.log("API Base URL:", apiBase);
  const openPDF = (id_stripping) => {
    const url = `${apiBase}/stripping/${id_stripping}/tally-pdf?t=${Date.now()}`;
    setPdfUrl(url);
    setShowPdfModal(true);
  };

  const fetchItems = async (page = 1, pageSize = defaultPageSize) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/stripping", {
        params: {
          page,
          limit: pageSize,
          customer_name: searchName || undefined,
          document_number: searchCode || undefined,
        },
      });

      const raw = res.data || {};

      // Backend returns { data: [...], pagination: { total, page, limit, totalPages } }
      const rows = raw.data || (Array.isArray(raw) ? raw : []);
      const paginationInfo = raw.pagination || {};

      const user = getUser();
      let finalRows = rows;
      if (user && user.id_level === 7) {
        finalRows = rows.filter(
          (item) =>
            item.id_customer === user.username ||
            item.customer_name === user.username ||
            item.nama_customer === user.username
        );
      }

      // Sort by stripping_date descending (newest first)
      finalRows.sort((a, b) => {
        const dateA = a.stripping_date ? new Date(a.stripping_date) : new Date(0);
        const dateB = b.stripping_date ? new Date(b.stripping_date) : new Date(0);
        return dateB - dateA;
      });

      // Use pagination.total from backend response
      const total = paginationInfo.total || rows.length;

      setItems(finalRows);
      setPagination({
        current: paginationInfo.page || page,
        pageSize: paginationInfo.limit || pageSize,
        total: total,
      });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar pembongkaran barang");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, defaultPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchItems(1, pagination.pageSize);
  };

  const handleResetFilter = () => {
    setSearchName("");
    setSearchCode("");
    fetchItems(1, defaultPageSize);
  };

  const handleTableChange = (pager) => {
    fetchItems(pager.current, pager.pageSize);
  };

  const handleExportExcel = () => {
    window.open(`${baseURL}/stripping/export`, "_blank");
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id_stripping);
    try {
      await axiosInstance.delete(`/stripping/${record.id_stripping}`);
      message.success("Pembongkaran barang berhasil dihapus");
      // reload current page
      fetchItems(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error(err);
      message.error("Gagal menghapus pembongkaran barang");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD-MM-YYYY") : "-";



  const columns = [
    {
      title: "NO",
      dataIndex: "index",
      width: 70,
      align: "center",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "NO PEMBONGKARAN",
      dataIndex: "stripping_number",
      width: 130,
    },
    {
      title: "TANGGAL PEMBONGKARAN",
      dataIndex: "stripping_date",
      width: 140,
      render: (value) => formatDate(value),
    },

    {
      title: "NO DOKUMEN",
      dataIndex: "document_number",
      width: 160,
      render: (value) => value || "-",
    },

    {
      title: "TOTAL KEMASAN",
      dataIndex: "total_kemasan",
      width: 160,
      render: (value) => value || "-",
    },

    {
      title: "AKSI",
      key: "action",
      width: 160,
      align: "center",
      render: (_, record) => (
        <Space>
          {/* PRINT / VIEW PDF */}
          <Tooltip title="Lihat & Cetak PDF">
            <Button
              size="small"
              type="primary"
              icon={<PrinterOutlined />}
              style={{
                background: "#3699FF",
                borderColor: "#3699FF",
              }}
              onClick={() => openPDF(record.id_stripping)}
            />
          </Tooltip>

          {/* EDIT */}
          {canEdit(MODULES.INVENTORY) && (
            <Tooltip title={getUser().id_level === 7 ? "Lihat Detil" : "Edit"}>
              <Link to={`updatetallysheet/${record.id_stripping}`}>
                <Button
                  size="small"
                  type="primary"
                  icon={getUser().id_level === 7 ? <SearchOutlined /> : <EditOutlined />}
                  style={{ background: "#3699FF", borderColor: "#3699FF" }}
                />
              </Link>
            </Tooltip>
          )}

          {/* DELETE */}
          {canDelete(MODULES.INVENTORY) && getUser().id_level !== 7 && (
            <Popconfirm
              title="Hapus Pembongkaran Barang"
              description={`Yakin ingin menghapus "${record.stripping_number}"?`}
              okText="Ya, Hapus"
              cancelText="Batal"
              onConfirm={() => handleDelete(record)}
              okButtonProps={{
                danger: true,
                loading: deletingId === record.id_stripping,
              }}
            >
              <Tooltip title="Hapus">
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={deletingId === record.id_stripping}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      {/* HEADER TITLE + ACTIONS */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Browse Pembongkaran Barang
          </Title>
          <Text type="secondary">
            Kelola daftar Pembongkaran Barang untuk kebutuhan inventory &
            kepabeanan.
          </Text>
        </Col>
        <Col xs={24} md={12} style={{ textAlign: { xs: 'left', md: 'right' } }}>
          <Space wrap>
            {canCreate(MODULES.INVENTORY) && getUser().id_level !== 7 && (
              <Link to="/inventory/tallying">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{
                    background: "#1BC5BD",
                    borderColor: "#1BC5BD",
                    fontWeight: 600,
                  }}
                >
                  Tambah
                </Button>
              </Link>
            )}
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              style={{
                background: "#1E1EFA",
                borderColor: "#1E1EFA",
                fontWeight: 600,
              }}
            >
              Excel
            </Button>
          </Space>
        </Col>
      </Row>

      {/* FILTER BAR */}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Text strong>Cari Berdasarkan Pemilik Barang</Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cari Item..."
            style={{ marginTop: 4 }}
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>

        <Col xs={24} md={12}>
          <Text strong>Cari Berdasarkan No Pendaftaran</Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cari kode..."
            style={{ marginTop: 4 }}
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>

        <Col xs={24} style={{ textAlign: "right" }}>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleResetFilter}>
              Reset
            </Button>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              style={{
                background: "#8950FC",
                borderColor: "#8950FC",
              }}
            >
              Apply Filter
            </Button>
          </Space>
        </Col>
      </Row>
      <Modal
        title="Tally Sheet PDF"
        open={showPdfModal}
        onCancel={() => setShowPdfModal(false)}
        width={900}
        footer={null}
        bodyStyle={{ padding: 0 }}
      >
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            title="Tally Sheet"
            style={{
              width: "100%",
              height: "80vh",
              border: "none",
            }}
          />
        )}
      </Modal>

      <Table
        style={{ marginTop: 36 }}
        rowKey="id_stripping"
        dataSource={items}
        columns={columns}
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `Total ${total} data | ${range[0]}-${range[1]}`,
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
}
